import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  TrendingUp, TrendingDown, AlertTriangle, Shield, Target, Plus,
  BarChart3, Calendar, CheckCircle, Clock, Loader2, RefreshCw,
  Send, MessageCircle, X, Minus, ChevronRight, Zap, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';

// ==========================================
// MONTHLY TRACKING TAB
// ==========================================
export const MonthlyTrackingTab = () => {
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => { fetchSnapshots(); }, []);

  const fetchSnapshots = async () => {
    setLoading(true);
    try {
      const res = await api.get('/performance/ai/monthly-snapshots');
      setSnapshots(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const generateSnapshot = async () => {
    setGenerating(true);
    try {
      const res = await api.post('/performance/ai/monthly-snapshot');
      toast.success(`Snapshot generated! Analyzed ${res.data.analyzed} employees`);
      fetchSnapshots();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to generate snapshot'); }
    finally { setGenerating(false); }
  };

  const getScoreColor = (s) => s >= 70 ? 'text-emerald-600' : s >= 50 ? 'text-blue-600' : s >= 30 ? 'text-amber-600' : 'text-red-600';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2"><Calendar className="h-5 w-5 text-indigo-600" /> Monthly Performance Tracking</h3>
          <p className="text-sm text-slate-500">AI-powered monthly snapshots track productivity trends, KPI achievement, and skill growth</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchSnapshots}><RefreshCw className="h-4 w-4 mr-1" /> Refresh</Button>
          <Button onClick={generateSnapshot} disabled={generating} className="bg-indigo-600 hover:bg-indigo-700" data-testid="generate-snapshot-btn">
            {generating ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Zap className="h-4 w-4 mr-1" />} Generate This Month
          </Button>
        </div>
      </div>

      {/* Snapshot Cards */}
      <div className="space-y-4">
        {snapshots.map((snap) => (
          <Card key={snap.id} className="hover:shadow-md transition-shadow" data-testid={`snapshot-${snap.month}`}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h4 className="font-semibold text-lg">{snap.month}</h4>
                  <p className="text-sm text-slate-500">{snap.analyzed} of {snap.total_employees} employees analyzed</p>
                </div>
                <div className="text-center">
                  <p className={`text-3xl font-bold ${getScoreColor(snap.average_score)}`}>{snap.average_score}</p>
                  <p className="text-xs text-slate-500">Avg Score</p>
                </div>
              </div>

              {/* Department Averages */}
              {snap.department_averages && Object.keys(snap.department_averages).length > 0 && (
                <div className="mb-4">
                  <p className="text-sm font-medium text-slate-600 mb-2">Department Scores</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {Object.entries(snap.department_averages).slice(0, 8).map(([dept, score]) => (
                      <div key={dept} className="p-2 bg-slate-50 rounded text-center">
                        <p className={`text-lg font-bold ${getScoreColor(score)}`}>{score}</p>
                        <p className="text-xs text-slate-500 truncate">{dept}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Trends */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-emerald-50 rounded-lg">
                  <p className="text-sm font-semibold text-emerald-700 flex items-center gap-1"><TrendingUp className="h-4 w-4" /> Trending Up</p>
                  {snap.trending_up?.slice(0, 3).map((e, i) => (
                    <p key={i} className="text-xs text-slate-600 mt-1">{e.name} - {e.score}</p>
                  )) || <p className="text-xs text-slate-400">None</p>}
                </div>
                <div className="p-3 bg-red-50 rounded-lg">
                  <p className="text-sm font-semibold text-red-700 flex items-center gap-1"><TrendingDown className="h-4 w-4" /> Needs Attention</p>
                  {snap.needs_attention?.slice(0, 3).map((e, i) => (
                    <p key={i} className="text-xs text-slate-600 mt-1">{e.name} - {e.score}</p>
                  )) || <p className="text-xs text-slate-400">None</p>}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {snapshots.length === 0 && !loading && (
          <Card className="p-8 text-center">
            <Calendar className="h-12 w-12 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">No monthly snapshots yet</p>
            <p className="text-sm text-slate-400 mt-1">Click "Generate This Month" to create the first snapshot</p>
          </Card>
        )}
      </div>

      {generating && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 shadow-2xl flex items-center gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
            <div><p className="font-semibold">Generating monthly snapshot...</p><p className="text-sm text-slate-500">Analyzing up to 50 employees with AI</p></div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==========================================
// BIAS DETECTION TAB
// ==========================================
export const BiasDetectionTab = ({ cycles }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [selectedCycle, setSelectedCycle] = useState('');
  const [selectedReport, setSelectedReport] = useState(null);

  useEffect(() => { fetchReports(); }, []);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const res = await api.get('/performance/ai/bias-reports');
      setReports(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const runDetection = async () => {
    setDetecting(true);
    try {
      const url = selectedCycle && selectedCycle !== 'all' ? `/performance/ai/detect-bias?cycle_id=${selectedCycle}` : '/performance/ai/detect-bias';
      const res = await api.post(url);
      toast.success('Bias detection complete');
      setSelectedReport(res.data);
      fetchReports();
    } catch (err) { toast.error(err.response?.data?.detail || 'Bias detection failed'); }
    finally { setDetecting(false); }
  };

  const getSeverityColor = (s) => ({
    'none': 'bg-emerald-100 text-emerald-700', 'low': 'bg-blue-100 text-blue-700',
    'medium': 'bg-amber-100 text-amber-700', 'high': 'bg-orange-100 text-orange-700', 'critical': 'bg-red-100 text-red-700'
  }[s] || 'bg-slate-100 text-slate-700');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2"><Shield className="h-5 w-5 text-indigo-600" /> Bias Detection</h3>
          <p className="text-sm text-slate-500">AI detects unfair evaluations, rating clustering, and favoritism patterns</p>
        </div>
        <div className="flex gap-2">
          <Select value={selectedCycle} onValueChange={setSelectedCycle}>
            <SelectTrigger className="w-48"><SelectValue placeholder="All Cycles" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Cycles</SelectItem>
              {cycles.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={runDetection} disabled={detecting} className="bg-indigo-600 hover:bg-indigo-700" data-testid="run-bias-detection-btn">
            {detecting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Shield className="h-4 w-4 mr-1" />} Run Detection
          </Button>
        </div>
      </div>

      {/* Active Report */}
      {selectedReport?.analysis && (
        <Card className={`border-2 ${selectedReport.analysis.bias_detected ? 'border-orange-300 bg-orange-50' : 'border-emerald-300 bg-emerald-50'}`}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                {selectedReport.analysis.bias_detected ? <AlertTriangle className="h-8 w-8 text-orange-600" /> : <CheckCircle className="h-8 w-8 text-emerald-600" />}
                <div>
                  <h4 className="font-semibold text-lg">{selectedReport.analysis.bias_detected ? 'Bias Patterns Detected' : 'No Significant Bias Found'}</h4>
                  <p className="text-sm text-slate-600">{selectedReport.reviews_analyzed} reviews analyzed across {selectedReport.managers_analyzed} managers</p>
                </div>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold">{selectedReport.analysis.overall_fairness_score}</p>
                <p className="text-xs text-slate-500">Fairness Score</p>
                <Badge className={getSeverityColor(selectedReport.analysis.severity)}>{selectedReport.analysis.severity}</Badge>
              </div>
            </div>

            <p className="text-sm mb-4">{selectedReport.analysis.summary}</p>

            {selectedReport.analysis.findings?.length > 0 && (
              <div className="space-y-3 mb-4">
                <p className="font-semibold text-sm">Findings:</p>
                {selectedReport.analysis.findings.map((f, i) => (
                  <div key={i} className="p-3 bg-white rounded-lg border">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className={getSeverityColor(f.severity)} variant="outline">{f.severity}</Badge>
                      <Badge variant="outline">{f.type?.replace('_', ' ')}</Badge>
                      {f.affected_manager && <span className="text-xs text-slate-500">Manager: {f.affected_manager}</span>}
                      {f.affected_department && <span className="text-xs text-slate-500">Dept: {f.affected_department}</span>}
                    </div>
                    <p className="text-sm">{f.description}</p>
                    <p className="text-xs text-slate-500 mt-1"><strong>Evidence:</strong> {f.evidence}</p>
                    <p className="text-xs text-indigo-600 mt-1"><strong>Recommendation:</strong> {f.recommendation}</p>
                  </div>
                ))}
              </div>
            )}

            {selectedReport.analysis.hr_recommendations?.length > 0 && (
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="font-semibold text-sm mb-2">HR Recommendations:</p>
                {selectedReport.analysis.hr_recommendations.map((r, i) => (
                  <p key={i} className="text-xs text-slate-600 flex items-start gap-1"><ChevronRight className="h-3 w-3 mt-0.5 text-blue-500 flex-shrink-0" /> {r}</p>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Previous Reports */}
      {reports.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Previous Reports</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {reports.map((r) => (
              <div key={r.id} className="flex items-center justify-between p-2 border rounded hover:bg-slate-50 cursor-pointer" onClick={() => setSelectedReport(r)}>
                <div className="flex items-center gap-2">
                  <Badge className={getSeverityColor(r.analysis?.severity)}>{r.analysis?.severity}</Badge>
                  <span className="text-sm">Fairness: {r.analysis?.overall_fairness_score}/100</span>
                  <span className="text-xs text-slate-500">{r.reviews_analyzed} reviews</span>
                </div>
                <span className="text-xs text-slate-400">{new Date(r.created_at).toLocaleDateString()}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {detecting && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 shadow-2xl flex items-center gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-indigo-600" /><div><p className="font-semibold">Running bias detection...</p><p className="text-sm text-slate-500">Analyzing review patterns with AI</p></div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==========================================
// OKR/KPI GOALS TAB
// ==========================================
export const GoalsTab = ({ employees }) => {
  const [goalsDashboard, setGoalsDashboard] = useState(null);
  const [employeeGoals, setEmployeeGoals] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [loading, setLoading] = useState(false);
  const [goalDialog, setGoalDialog] = useState(false);
  const [updateDialog, setUpdateDialog] = useState(null);
  const [updateValue, setUpdateValue] = useState('');
  const [goalForm, setGoalForm] = useState({
    title: '', description: '', type: 'KPI', target_value: '', target_unit: '%',
    current_value: '0', start_date: '', due_date: '', priority: 'medium'
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insights, setInsights] = useState(null);

  useEffect(() => { fetchDashboard(); }, []);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const res = await api.get('/performance/ai/goals-dashboard');
      setGoalsDashboard(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchEmployeeGoals = async (empId) => {
    try {
      const res = await api.get(`/performance/ai/goals/${empId}`);
      setEmployeeGoals(res.data);
    } catch (err) { console.error(err); }
  };

  const createGoal = async () => {
    if (!selectedEmployee || !goalForm.title) return;
    try {
      const payload = { ...goalForm };
      if (payload.target_value) payload.target_value = parseFloat(payload.target_value);
      if (payload.current_value) payload.current_value = parseFloat(payload.current_value);
      await api.post(`/performance/ai/goals/${selectedEmployee.id}`, payload);
      toast.success('Goal created');
      setGoalDialog(false);
      setGoalForm({ title: '', description: '', type: 'KPI', target_value: '', target_unit: '%', current_value: '0', start_date: '', due_date: '', priority: 'medium' });
      fetchEmployeeGoals(selectedEmployee.id);
      fetchDashboard();
    } catch (err) { toast.error('Failed to create goal'); }
  };

  const updateGoalProgress = async (goal) => {
    try {
      await api.put(`/performance/ai/goals/${goal.employee_id}/${goal.id}?current_value=${updateValue}`);
      toast.success('Goal updated');
      setUpdateDialog(null);
      setUpdateValue('');
      fetchEmployeeGoals(goal.employee_id);
      fetchDashboard();
    } catch (err) { toast.error('Failed to update'); }
  };

  const getGoalInsights = async (empId) => {
    setInsightsLoading(true);
    try {
      const res = await api.post(`/performance/ai/goals-ai-insights/${empId}`);
      setInsights(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to get insights'); }
    finally { setInsightsLoading(false); }
  };

  const getStatusColor = (s) => ({
    'completed': 'bg-emerald-100 text-emerald-700', 'on_track': 'bg-blue-100 text-blue-700',
    'at_risk': 'bg-amber-100 text-amber-700', 'behind': 'bg-red-100 text-red-700'
  }[s] || 'bg-slate-100 text-slate-700');

  const filteredEmployees = employees.filter(e =>
    e.name?.toLowerCase().includes(searchQuery.toLowerCase()) || e.department?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2"><Target className="h-5 w-5 text-indigo-600" /> OKR / KPI Goal Tracking</h3>
          <p className="text-sm text-slate-500">Track revenue, hiring, project delivery, and client satisfaction goals with AI insights</p>
        </div>
      </div>

      {/* Dashboard Summary */}
      {goalsDashboard && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <Card className="bg-indigo-50 border-indigo-200"><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-indigo-700">{goalsDashboard.total_goals}</p><p className="text-xs text-slate-500">Total Goals</p></CardContent></Card>
          <Card className="bg-emerald-50 border-emerald-200"><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-emerald-700">{goalsDashboard.completed}</p><p className="text-xs text-slate-500">Completed</p></CardContent></Card>
          <Card className="bg-blue-50 border-blue-200"><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-blue-700">{goalsDashboard.on_track}</p><p className="text-xs text-slate-500">On Track</p></CardContent></Card>
          <Card className="bg-amber-50 border-amber-200"><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-amber-700">{goalsDashboard.at_risk}</p><p className="text-xs text-slate-500">At Risk</p></CardContent></Card>
          <Card className="bg-red-50 border-red-200"><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-red-700">{goalsDashboard.behind}</p><p className="text-xs text-slate-500">Behind</p></CardContent></Card>
        </div>
      )}

      {/* Employee Goal Management */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="md:col-span-1">
          <CardHeader className="pb-2"><CardTitle className="text-sm">Select Employee</CardTitle></CardHeader>
          <CardContent>
            <Input placeholder="Search..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="mb-2" autoComplete="off" />
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {filteredEmployees.slice(0, 15).map(emp => (
                <div key={emp.id} className={`p-2 rounded cursor-pointer text-sm transition-colors ${selectedEmployee?.id === emp.id ? 'bg-indigo-100 text-indigo-700' : 'hover:bg-slate-50'}`}
                  onClick={() => { setSelectedEmployee(emp); fetchEmployeeGoals(emp.id); setInsights(null); }}>
                  <p className="font-medium">{emp.name}</p>
                  <p className="text-xs text-slate-500">{emp.department}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="md:col-span-2">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm">{selectedEmployee ? `Goals: ${selectedEmployee.name}` : 'Select an employee'}</CardTitle>
              {selectedEmployee && (
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => getGoalInsights(selectedEmployee.id)} disabled={insightsLoading}>
                    {insightsLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Zap className="h-3 w-3" />}
                  </Button>
                  <Button size="sm" onClick={() => setGoalDialog(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-goal-btn">
                    <Plus className="h-3 w-3 mr-1" /> Add Goal
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {employeeGoals.length > 0 ? (
              <div className="space-y-3">
                {employeeGoals.map(goal => (
                  <div key={goal.id} className="p-3 border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Badge className={getStatusColor(goal.status)}>{goal.status?.replace('_', ' ')}</Badge>
                        <Badge variant="outline">{goal.type}</Badge>
                        <Badge variant="outline" className={goal.priority === 'high' ? 'border-red-300' : goal.priority === 'critical' ? 'border-red-500' : ''}>{goal.priority}</Badge>
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => { setUpdateDialog(goal); setUpdateValue(goal.current_value?.toString() || '0'); }}>
                        <Eye className="h-3 w-3 mr-1" /> Update
                      </Button>
                    </div>
                    <p className="font-medium text-sm">{goal.title}</p>
                    {goal.description && <p className="text-xs text-slate-500 mt-1">{goal.description}</p>}
                    <div className="mt-2">
                      <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                        <span>{goal.current_value || 0} / {goal.target_value} {goal.target_unit}</span>
                        <span>{goal.progress}%</span>
                      </div>
                      <Progress value={goal.progress || 0} className="h-2" />
                    </div>
                    {goal.due_date && <p className="text-xs text-slate-400 mt-1">Due: {goal.due_date}</p>}
                  </div>
                ))}
              </div>
            ) : selectedEmployee ? (
              <p className="text-sm text-slate-400 text-center py-4">No goals set. Click "Add Goal" to create one.</p>
            ) : (
              <p className="text-sm text-slate-400 text-center py-4">Select an employee to manage goals</p>
            )}

            {insights && (
              <div className="mt-4 p-3 bg-indigo-50 rounded-lg">
                <p className="font-semibold text-sm mb-2">AI Goal Insights</p>
                <div className="flex gap-4 mb-2">
                  <span className="text-xs">Progress: <strong>{insights.insights?.overall_progress}%</strong></span>
                  <span className="text-xs">Risk: <strong>{insights.insights?.risk_assessment}</strong></span>
                  <span className="text-xs">Prediction: <strong>{insights.insights?.predicted_completion}</strong></span>
                </div>
                {insights.insights?.recommendations?.map((r, i) => (
                  <p key={i} className="text-xs text-slate-600 flex items-start gap-1"><ChevronRight className="h-3 w-3 mt-0.5 text-indigo-500 flex-shrink-0" /> {r}</p>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create Goal Dialog */}
      <Dialog open={goalDialog} onOpenChange={setGoalDialog}>
        <DialogContent onInteractOutside={(e) => e.preventDefault()} onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader><DialogTitle>Create Goal for {selectedEmployee?.name}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-3">
            <div className="col-span-2"><Label>Title</Label><Input value={goalForm.title} onChange={(e) => setGoalForm(p => ({...p, title: e.target.value}))} placeholder="e.g., Increase quarterly revenue" autoComplete="off" /></div>
            <div><Label>Type</Label>
              <Select value={goalForm.type} onValueChange={(v) => setGoalForm(p => ({...p, type: v}))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="KPI">KPI</SelectItem><SelectItem value="OKR">OKR</SelectItem><SelectItem value="Revenue">Revenue</SelectItem><SelectItem value="Project">Project</SelectItem><SelectItem value="Hiring">Hiring</SelectItem><SelectItem value="Custom">Custom</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Priority</Label>
              <Select value={goalForm.priority} onValueChange={(v) => setGoalForm(p => ({...p, priority: v}))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem><SelectItem value="critical">Critical</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Target Value</Label><Input type="number" value={goalForm.target_value} onChange={(e) => setGoalForm(p => ({...p, target_value: e.target.value}))} placeholder="100" autoComplete="off" /></div>
            <div><Label>Unit</Label><Input value={goalForm.target_unit} onChange={(e) => setGoalForm(p => ({...p, target_unit: e.target.value}))} placeholder="%, $, units" autoComplete="off" /></div>
            <div><Label>Start Date</Label><Input type="date" value={goalForm.start_date} onChange={(e) => setGoalForm(p => ({...p, start_date: e.target.value}))} /></div>
            <div><Label>Due Date</Label><Input type="date" value={goalForm.due_date} onChange={(e) => setGoalForm(p => ({...p, due_date: e.target.value}))} /></div>
            <div className="col-span-2"><Label>Description</Label><Textarea value={goalForm.description} onChange={(e) => setGoalForm(p => ({...p, description: e.target.value}))} placeholder="Goal details..." autoComplete="off" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setGoalDialog(false)}>Cancel</Button><Button onClick={createGoal} className="bg-indigo-600 hover:bg-indigo-700">Create Goal</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update Goal Dialog */}
      <Dialog open={!!updateDialog} onOpenChange={() => setUpdateDialog(null)}>
        <DialogContent onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader><DialogTitle>Update Progress: {updateDialog?.title}</DialogTitle></DialogHeader>
          <div className="py-4">
            <Label>Current Value (Target: {updateDialog?.target_value} {updateDialog?.target_unit})</Label>
            <Input type="number" value={updateValue} onChange={(e) => setUpdateValue(e.target.value)} autoComplete="off" />
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setUpdateDialog(null)}>Cancel</Button><Button onClick={() => updateGoalProgress(updateDialog)} className="bg-indigo-600 hover:bg-indigo-700">Update</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==========================================
// AI CHATBOT WIDGET
// ==========================================
export const AIChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'ai', text: 'Hi! I can help you with performance-related questions. Ask me about KPI progress, improvement suggestions, or training recommendations.' }
  ]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || sending) return;
    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: userMsg }]);
    setSending(true);

    try {
      const res = await api.post('/performance/ai/chat', { message: userMsg });
      setMessages(prev => [...prev, { role: 'ai', text: res.data.response }]);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'ai', text: 'Sorry, I encountered an error. Please try again.' }]);
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 w-14 h-14 bg-indigo-600 hover:bg-indigo-700 rounded-full shadow-xl flex items-center justify-center text-white transition-all hover:scale-110 z-50"
        data-testid="ai-chat-toggle"
      >
        <MessageCircle className="h-6 w-6" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 w-96 h-[500px] bg-white rounded-2xl shadow-2xl border flex flex-col z-50" data-testid="ai-chat-widget">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-indigo-600 to-purple-600 rounded-t-2xl">
        <div className="flex items-center gap-2 text-white">
          <MessageCircle className="h-5 w-5" />
          <span className="font-semibold">AI Performance Assistant</span>
        </div>
        <button onClick={() => setIsOpen(false)} className="text-white/80 hover:text-white"><X className="h-5 w-5" /></button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] p-3 rounded-2xl text-sm ${
              msg.role === 'user'
                ? 'bg-indigo-600 text-white rounded-br-sm'
                : 'bg-slate-100 text-slate-700 rounded-bl-sm'
            }`}>
              <p className="whitespace-pre-wrap">{msg.text}</p>
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="bg-slate-100 p-3 rounded-2xl rounded-bl-sm">
              <div className="flex gap-1"><div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" /><div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{animationDelay:'0.1s'}} /><div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{animationDelay:'0.2s'}} /></div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-3 border-t">
        <div className="flex gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
            placeholder="Ask about performance..."
            className="flex-1"
            autoComplete="off"
            data-testid="ai-chat-input"
          />
          <Button onClick={sendMessage} disabled={sending || !input.trim()} size="icon" className="bg-indigo-600 hover:bg-indigo-700" data-testid="ai-chat-send">
            <Send className="h-4 w-4" />
          </Button>
        </div>
        <p className="text-xs text-slate-400 mt-1 text-center">Powered by GPT-5.2</p>
      </div>
    </div>
  );
};
